import { push } from "connected-react-router";
import { normalize } from "normalizr";
import { matchPath } from "react-router";
import { all, call, delay, put, select, takeLatest } from "redux-saga/effects";

import { LastVisitCounts, forumHeaderUpdate } from "@/mk2/apps/forum/actions";
import { COMMON_PLACE_CATEGORY_SLUG } from "@/mk2/apps/forum/constants";
import { CategoryEntity, CategorySchema, TopicEntity, TopicSchema } from "@/mk2/apps/forum/schemas";
import {
    forumCommonPlaceTopicsUrl,
    forumPostNewInCategoryUrl,
    forumPostNewUrl,
} from "@/mk2/apps/forum/urls";
import { XHRAction, XHRGetError, handleXHRGetErrorSaga, normalizeError } from "@/mk2/helpers/api";
import { url } from "@/mk2/helpers/urls";
import { getLogger } from "@/mk2/logger";
import { forum_api_common_place_topics_url } from "@/mk/urls/functions";

import {
    COMMON_PLACE_TOPICS_LOAD,
    COMMON_PLACE_TOPICS_SEARCH,
    CommonPlaceTopicsLoadAction,
    CommonPlaceTopicsSearchAction,
    commonPlaceTopicsFetch,
    commonPlaceTopicsLoad,
    commonPlaceTopicsUpdateSearch,
} from "./CommonPlaceTopics.actions";

const logger = getLogger("forum/CommonPlaceTopics.sagas");

interface ApiResponse {
    body: {
        category: CategoryEntity;
        followingTopics: TopicEntity[];
        topics: TopicEntity[];
        hasNext: number;
        hasPrevious: number;
        lastVisitCounts: LastVisitCounts;
    };
}

function* fetchTopics({ after, placeType, search, xhr }: CommonPlaceTopicsLoadAction & XHRAction) {
    yield put(commonPlaceTopicsFetch.request(placeType, search, after));

    try {
        const response: ApiResponse = yield call(() =>
            xhr.get(forum_api_common_place_topics_url(), { type: placeType, search, after }),
        );
        const normalizedResponse = normalize(response.body, {
            category: CategorySchema,
            followingTopics: [TopicSchema],
            topics: [TopicSchema],
        });
        yield put(
            commonPlaceTopicsFetch.success(
                placeType,
                search,
                after,
                response.body.hasNext,
                response.body.hasPrevious,
                normalizedResponse,
            ),
        );
        yield put(forumHeaderUpdate(response.body.lastVisitCounts));
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, logger);
        yield put(commonPlaceTopicsFetch.failure(placeType, search, after, normalizeError(error)));
    }
}

function* searchTopics({ placeType, search, location }: CommonPlaceTopicsSearchAction) {
    yield put(commonPlaceTopicsUpdateSearch(search));

    // Debounce
    yield delay(300);

    // Run load
    yield put(commonPlaceTopicsLoad(placeType, search));

    const match = matchPath(location.pathname, { path: forumPostNewUrl, exact: false });

    if (match) {
        // We are creating new post
        // Update URL
        if (search) {
            yield put(
                push(
                    url(
                        forumPostNewInCategoryUrl,
                        { categorySlug: COMMON_PLACE_CATEGORY_SLUG },
                        { search },
                    ),
                ),
            );
        } else {
            yield put(
                push(url(forumPostNewInCategoryUrl, { categorySlug: COMMON_PLACE_CATEGORY_SLUG })),
            );
        }
    } else {
        // Update URL
        if (search) {
            yield put(push(url(forumCommonPlaceTopicsUrl, {}, { search })));
        } else {
            yield put(push(forumCommonPlaceTopicsUrl));
        }
    }
}

export default function* root() {
    yield all([
        takeLatest(COMMON_PLACE_TOPICS_LOAD, fetchTopics),
        takeLatest(COMMON_PLACE_TOPICS_SEARCH, searchTopics),
    ]);
}
